import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useFraudDashboardGauges } from '@/api/hooks/useFraudDashboard';
import DetectiveChips from '@/components/ai/DetectiveChips';
import { SEVERITY_BADGE_CLASS, SEVERITY_FILL } from '@/lib/severity';
import { TimePeriodSelector } from '@/components/shared/TimePeriodSelector';
import { FraudKpiRow } from '@/components/fraud-dashboard/FraudKpiRow';
import { AlertTrendChart } from '@/components/fraud-dashboard/AlertTrendChart';
import { FraudByMakeChart } from '@/components/fraud-dashboard/FraudByMakeChart';
import { TopRulesBar } from '@/components/fraud-dashboard/TopRulesBar';
import { CriticalBoardGrid } from '@/components/fraud-dashboard/CriticalBoardGrid';
import type {
  FraudDashboardCategory,
  FraudDashboardRule,
} from '@/types/fraudDashboard';
import type { Severity } from '@/types/alerts';

/**
 * Per-severity gauge-card frame (border colour). Badge colours come
 * from `SEVERITY_BADGE_CLASS` (shared muted variant) and arc fills
 * come from `SEVERITY_FILL` (shared HSL palette) — both lifted out of
 * this file in Fraud Hub PR 2.
 */
const SEVERITY_BORDER: Record<Severity, string> = {
  Critical: 'border-red-200',
  High: 'border-orange-200',
  Medium: 'border-amber-200',
  Low: 'border-blue-200',
  Watch: 'border-slate-200',
  Normal: 'border-emerald-200',
};

// Display order matches the screenshot. Categories in the API but not
// listed here are appended at the bottom in API order.
const CATEGORY_DISPLAY_ORDER = [
  'OD',
  'VOL',
  'TM',
  'ST',
  'CD',
  'PA',
  'CP',
  'KE',
  'PR',
  'PD',
  'OIL',
  'MV',
  'TOLL',
];

const CATEGORY_DISPLAY_LABEL: Record<string, string> = {
  OD: 'Odometer',
  VOL: 'Volume',
  TM: 'Time',
  ST: 'Location',
  CD: 'Card',
  PA: 'Pump Attendant',
  CP: 'Consumption',
  KE: 'Efficiency',
  PR: 'Price',
  PD: 'Product',
  OIL: 'Oil & Product',
  MV: 'Movement',
  TOLL: 'Toll',
};

export default function FraudDashboard() {
  const { data, isLoading, error } = useFraudDashboardGauges();

  const orderedCategories = useMemo<FraudDashboardCategory[]>(() => {
    if (!data) return [];
    const indexed = new Map<string, FraudDashboardCategory>();
    for (const c of data.categories) indexed.set(c.category_code, c);
    const ordered: FraudDashboardCategory[] = [];
    for (const code of CATEGORY_DISPLAY_ORDER) {
      const c = indexed.get(code);
      if (c) {
        ordered.push(c);
        indexed.delete(code);
      }
    }
    for (const c of indexed.values()) ordered.push(c);
    return ordered;
  }, [data]);

  return (
    <div className="flex flex-col gap-6">
      {/* Period selector sits above the header — every widget below (KPIs,
          alert-trend, by-make, top-rules) re-queries on period change. The
          Critical board is period-INDEPENDENT (open is a status). */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fuel Fraud Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Period-aware fraud posture — alerts, trend, and offending makes.
          </p>
        </div>
        {/* LTD, not M. These widgets window on fraud.alert.alert_time, which
            records when the detector RAISED the alert, not when the fraud
            happened — 95.6% of alerts carry a single bulk-backfill timestamp.
            "M" therefore showed 17,280 of 604,010 alerts (2.9%): one rule, two
            bars, zero Critical. Until alert_time is re-anchored to transaction
            time, LTD is the only period that reflects the real posture. */}
        <TimePeriodSelector defaultPeriod="LTD" />
      </div>

      <DetectiveChips module="fraud-dashboard" />

      {/* Section A — fraud KPI row (delta pills from the KpiMetric envelope). */}
      <FraudKpiRow />

      {/* Section B — alert trend (stacked severity) + alerts by make. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <AlertTrendChart />
        <FraudByMakeChart />
      </div>

      {/* Section C — top firing rules (bar). */}
      <TopRulesBar />

      {/* Section D — critical alerts board (DataGridPro). */}
      <CriticalBoardGrid />

      {/* Section E — category posture gauges (per-category open counts). */}
      <div className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Category posture
        </h2>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-3 text-sm text-red-800">
              Could not load gauges: {(error as Error).message}
            </CardContent>
          </Card>
        )}

        {isLoading && <DashboardSkeleton />}

        {!isLoading &&
          orderedCategories.map((category) => (
            <CategorySection key={category.category_code} category={category} />
          ))}

        {!isLoading && !error && orderedCategories.length === 0 && (
          <p className="text-sm text-muted-foreground">No active fraud rules.</p>
        )}
      </div>
    </div>
  );
}

function CategorySection({ category }: { category: FraudDashboardCategory }) {
  const navigate = useNavigate();
  const displayLabel =
    CATEGORY_DISPLAY_LABEL[category.category_code] ?? category.category_label;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {displayLabel}
      </h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {category.rules.map((rule) => (
          <RuleGaugeCard
            key={rule.rule_id}
            rule={rule}
            categoryCode={category.category_code}
            onClick={() =>
              navigate(
                `/fraud?rule_id=${encodeURIComponent(rule.rule_id)}` +
                  `&category=${encodeURIComponent(category.category_code)}`,
              )
            }
          />
        ))}
      </div>
    </section>
  );
}

interface RuleGaugeCardProps {
  rule: FraudDashboardRule;
  categoryCode: string;
  onClick: () => void;
}

function RuleGaugeCard({ rule, onClick }: RuleGaugeCardProps) {
  const border = SEVERITY_BORDER[rule.severity] ?? SEVERITY_BORDER.Normal;
  const fill = SEVERITY_FILL[rule.severity] ?? SEVERITY_FILL.Normal;
  const badgeClass =
    SEVERITY_BADGE_CLASS[rule.severity] ?? SEVERITY_BADGE_CLASS.Normal;
  const Icon = resolveIcon(rule.icon);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      title={`${rule.open_alert_count.toLocaleString('en-ZA')} open alert${
        rule.open_alert_count === 1 ? '' : 's'
      }`}
      className={cn(
        'cursor-pointer transition-all hover:border-foreground/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        border,
      )}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <SemicircleGauge count={rule.open_alert_count} fill={fill} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm font-semibold">{rule.label}</span>
          </div>
          {rule.blurb && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{rule.blurb}</p>
          )}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn('font-medium', badgeClass)}>
              {rule.severity}
            </Badge>
            <span className="text-xs tabular-nums text-muted-foreground">
              {rule.open_alert_count.toLocaleString('en-ZA')} open
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface SemicircleGaugeProps {
  count: number;
  fill: string;
}

// Caps log-fill at this count so 1 alert -> small slice, ~100 -> half,
// 10000+ -> full arc. Picked so noisy rules don't peg every gauge to 100%.
const GAUGE_FILL_CAP = 10000;

function formatCompactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1000000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1000000).toFixed(1)}M`;
}

function SemicircleGauge({ count, fill }: SemicircleGaugeProps) {
  const safeCount = Math.max(0, count);
  // Log-scaled fill — count=0 -> 0, count=GAUGE_FILL_CAP -> 1.
  const fillRatio =
    safeCount === 0
      ? 0
      : Math.min(1, Math.log10(1 + safeCount) / Math.log10(1 + GAUGE_FILL_CAP));
  // Semicircle path geometry: 80x40 viewBox, arc radius 32, centre (40,40).
  const radius = 32;
  const cx = 40;
  const cy = 40;
  const startAngle = Math.PI; // left side, 180°
  const endAngle = startAngle + fillRatio * Math.PI;
  const trackEnd = 0; // right side, 0°
  const trackPath = describeArc(cx, cy, radius, startAngle, trackEnd);
  const valuePath = describeArc(cx, cy, radius, startAngle, endAngle);
  const display = formatCompactCount(safeCount);

  return (
    <div className="relative h-16 w-20 shrink-0">
      <svg viewBox="0 0 80 44" className="h-full w-full" aria-hidden>
        <path
          d={trackPath}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={6}
          strokeLinecap="round"
        />
        {fillRatio > 0 && (
          <path
            d={valuePath}
            fill="none"
            stroke={fill}
            strokeWidth={6}
            strokeLinecap="round"
          />
        )}
      </svg>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center">
        {/* text-sm (was text-base) — compact form like "3.3K" needs slightly tighter glyphs to clear the SVG arc. */}
        <span className="text-sm font-semibold tabular-nums">{display}</span>
      </div>
    </div>
  );
}

function describeArc(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = polar(cx, cy, r, startAngle);
  const end = polar(cx, cy, r, endAngle);
  const largeArc = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;
  // Sweep flag 1 — clockwise from left (180°) toward right (0°), the
  // direction that traces the upper semicircle in SVG's y-down space.
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function polar(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

function resolveIcon(name: string): LucideIcons.LucideIcon {
  const pascal = name
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const Icon = (LucideIcons as unknown as Record<string, LucideIcons.LucideIcon>)[
    pascal
  ];
  return Icon ?? LucideIcons.ShieldAlert;
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      {Array.from({ length: 3 }).map((_, sectionIdx) => (
        <section key={sectionIdx} className="flex flex-col gap-3">
          <Skeleton className="h-3 w-24" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((__, cardIdx) => (
              <Card key={cardIdx}>
                <CardContent className="flex items-center gap-4 p-4">
                  <Skeleton className="h-16 w-20 shrink-0 rounded-md" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
